import {
  AGENT_READINESS_POSTHOG_EVENT_NAMES,
  POSTHOG_EVENT_NAMES,
  POSTHOG_EVENT_PAYLOAD_KEYS,
  type PostHogEventMap,
  type PostHogEventPayload,
} from "./events";

export const AGENT_READINESS_TAXONOMY_VERSION = "agent-readiness-audit-2026-06-15";
export type AgentReadinessPostHogEventName = (typeof AGENT_READINESS_POSTHOG_EVENT_NAMES)[number];

const SLUG_FALLBACK = "unknown";
const TEXT_FORBIDDEN_PATTERNS = [
  /@/,
  /\bapi[_-]?key\b/i,
  /\baccess[_-]?token\b/i,
  /\brefresh[_-]?token\b/i,
  /\bsecret\b/i,
  /\bpassword\b/i,
  /\bauthorization\b/i,
];

type TaskStepResult = "pass" | "fail" | "blocked" | "not_run" | "not_determinable";
type AuditVerdict = "FINDING" | "PASS" | "N_A" | "N_A_DEPENDENT" | "NOT_DETERMINABLE";
type AuditLane = "00_BLOCKED" | "01_NEEDS_INPUT" | "02_READY_FOR_CONTENT" | "03_READY_FOR_DEV" | "none";
type AuditPriority = "P0" | "P1" | "P2" | "P3";
type AuditConfidence = "high" | "medium" | "low";
type AgentSurface = "site" | "calendar" | "posthog" | "crm" | "repo" | "unknown";
type ArtifactKind = "dom" | "screenshot" | "log" | "csv" | "json" | "browser_text" | "unknown";
type CommercialImpactBand = "none" | "lead_risk" | "quote_risk" | "booking_risk" | "revenue_risk";
type DelegationSurface = "none" | "calendar" | "form" | "api" | "email" | "crm";
type RegressionStatus = "not_configured" | "configured_repo" | "configured_live" | "not_applicable";
type TrackingSurfaceState = "not_defined" | "defined_repo" | "defined_live" | "observed_live";
type OperatorFeedbackState = "not_requested" | "requested" | "received" | "triaged" | "not_applicable";
type SourceEventKind = "browser_event" | "server_event" | "crm_event" | "batch_export" | "manual_import";

export type AgentReadinessTaskStepInput = {
  readonly agentEngine?: unknown;
  readonly agentRunId?: unknown;
  readonly agentStepId?: unknown;
  readonly agentSurface?: unknown;
  readonly agentTaskId?: unknown;
  readonly artifactKind?: unknown;
  readonly artifactPath?: unknown;
  readonly batterySubmitPath?: unknown;
  readonly blockedReason?: unknown;
  readonly commercialImpactBand?: unknown;
  readonly delegationSurface?: unknown;
  readonly durationMs?: unknown;
  readonly result?: unknown;
  readonly routePath?: unknown;
  readonly sourceEventKind?: unknown;
  readonly trackingSurfaceState?: unknown;
};

export type AgentReadinessSnapshotInput = {
  readonly agentEngine?: unknown;
  readonly agentRunId?: unknown;
  readonly artifactKind?: unknown;
  readonly artifactPath?: unknown;
  readonly category?: unknown;
  readonly checkCount?: unknown;
  readonly confidence?: unknown;
  readonly failedCount?: unknown;
  readonly lane?: unknown;
  readonly lensSlug?: unknown;
  readonly naCount?: unknown;
  readonly notDeterminableCount?: unknown;
  readonly priority?: unknown;
  readonly regressionStatus?: unknown;
  readonly sourceEventKind?: unknown;
  readonly ticketCount?: unknown;
  readonly trackingSurfaceState?: unknown;
  readonly verdict?: unknown;
};

export type AgentReadinessOperatorFeedbackInput = AgentReadinessSnapshotInput & {
  readonly operatorFeedbackState?: unknown;
};

const taskStepResults: readonly TaskStepResult[] = ["pass", "fail", "blocked", "not_run", "not_determinable"];
const auditVerdicts: readonly AuditVerdict[] = ["FINDING", "PASS", "N_A", "N_A_DEPENDENT", "NOT_DETERMINABLE"];
const auditLanes: readonly AuditLane[] = ["00_BLOCKED", "01_NEEDS_INPUT", "02_READY_FOR_CONTENT", "03_READY_FOR_DEV", "none"];
const auditPriorities: readonly AuditPriority[] = ["P0", "P1", "P2", "P3"];
const auditConfidences: readonly AuditConfidence[] = ["high", "medium", "low"];
const agentSurfaces: readonly AgentSurface[] = ["site", "calendar", "posthog", "crm", "repo", "unknown"];
const artifactKinds: readonly ArtifactKind[] = ["dom", "screenshot", "log", "csv", "json", "browser_text", "unknown"];
const commercialImpactBands: readonly CommercialImpactBand[] = [
  "none",
  "lead_risk",
  "quote_risk",
  "booking_risk",
  "revenue_risk",
];
const delegationSurfaces: readonly DelegationSurface[] = ["none", "calendar", "form", "api", "email", "crm"];
const regressionStatuses: readonly RegressionStatus[] = [
  "not_configured",
  "configured_repo",
  "configured_live",
  "not_applicable",
];
const trackingSurfaceStates: readonly TrackingSurfaceState[] = [
  "not_defined",
  "defined_repo",
  "defined_live",
  "observed_live",
];
const operatorFeedbackStates: readonly OperatorFeedbackState[] = [
  "not_requested",
  "requested",
  "received",
  "triaged",
  "not_applicable",
];
const sourceEventKinds: readonly SourceEventKind[] = [
  "browser_event",
  "server_event",
  "crm_event",
  "batch_export",
  "manual_import",
];

function isForbiddenText(value: string): boolean {
  return TEXT_FORBIDDEN_PATTERNS.some((pattern) => pattern.test(value));
}

function safeSlug(value: unknown, fallback = SLUG_FALLBACK, maxLength = 96): string {
  if (value === undefined || value === null) {
    return fallback;
  }

  const raw = String(value).trim();
  if (!raw || isForbiddenText(raw)) {
    return fallback;
  }

  const normalized = raw
    .replace(/\\/g, "/")
    .replace(/^[A-Za-z]:\/+/g, "")
    .replace(/[^A-Za-z0-9_./:-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-./:]+|[-./:]+$/g, "")
    .toLowerCase();

  return normalized.slice(0, maxLength) || fallback;
}

function safeRoutePath(value: unknown): string | undefined {
  const route = safeSlug(value, "");
  return route ? `/${route.replace(/^\/+/, "")}` : undefined;
}

function safeArtifactPath(value: unknown): string | undefined {
  const artifactPath = safeSlug(value, "", 160);
  if (!artifactPath) {
    return undefined;
  }

  return artifactPath
    .replace(/^skills\/agent-readiness-audit\/jira\/seoh-team-en\//, "")
    .replace(/^agent-readiness-audit\/jira\/seoh-team-en\//, "");
}

function enumValue<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function numberValue(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return undefined;
  }

  return Math.round(value);
}

function agentEngineFamily(agentEngine: string): "chrome" | "other" {
  return agentEngine.includes("chrome") ? "chrome" : "other";
}

function compactPayload<T extends PostHogEventPayload>(payload: T): T {
  const compactedEntries = Object.entries(payload).filter(([, value]) => value !== undefined && value !== "");
  for (const [key, value] of compactedEntries) {
    if (!POSTHOG_EVENT_PAYLOAD_KEYS.has(key)) {
      throw new Error(`Agent readiness payload key is not allow-listed for PostHog: ${key}`);
    }
    if (typeof value === "string" && isForbiddenText(value)) {
      throw new Error(`Agent readiness payload contains forbidden text in key: ${key}`);
    }
  }

  return Object.fromEntries(compactedEntries) as T;
}

export function isAgentReadinessEventName(eventName: string): eventName is AgentReadinessPostHogEventName {
  return AGENT_READINESS_POSTHOG_EVENT_NAMES.includes(eventName as AgentReadinessPostHogEventName);
}

export function assertAgentReadinessTrackingContract(): void {
  for (const eventName of AGENT_READINESS_POSTHOG_EVENT_NAMES) {
    if (!POSTHOG_EVENT_NAMES.has(eventName)) {
      throw new Error(`Agent readiness event is missing from POSTHOG_EVENT_NAMES: ${eventName}`);
    }
  }
}

export function buildAgentReadinessTaskStepPayload(
  input: AgentReadinessTaskStepInput,
): PostHogEventMap["agent_readiness_task_step"] {
  const agentEngine = safeSlug(input.agentEngine);

  return compactPayload({
    agent_engine: agentEngine,
    agent_engine_family: agentEngineFamily(agentEngine),
    agent_session_class: "agent",
    agent_run_id: safeSlug(input.agentRunId),
    agent_step_id: safeSlug(input.agentStepId),
    agent_step_result: enumValue(input.result, taskStepResults, "not_determinable"),
    agent_surface: enumValue(input.agentSurface, agentSurfaces, "unknown"),
    agent_task_id: safeSlug(input.agentTaskId),
    audit_artifact_kind: enumValue(input.artifactKind, artifactKinds, "unknown"),
    audit_artifact_path: safeArtifactPath(input.artifactPath),
    battery_submit_path: enumValue(input.batterySubmitPath, ["pre_submit", "test_payload", "not_applicable"], "not_applicable"),
    blocked_reason: safeSlug(input.blockedReason),
    commercial_impact_band: enumValue(input.commercialImpactBand, commercialImpactBands, "none"),
    delegation_surface: enumValue(input.delegationSurface, delegationSurfaces, "none"),
    duration_ms: numberValue(input.durationMs),
    route_path: safeRoutePath(input.routePath),
    source_event_kind: enumValue(input.sourceEventKind, sourceEventKinds, "manual_import"),
    taxonomy_version: AGENT_READINESS_TAXONOMY_VERSION,
    tracking_surface_state: enumValue(input.trackingSurfaceState, trackingSurfaceStates, "defined_repo"),
  });
}

export function buildAgentReadinessAuditSnapshotPayload(
  input: AgentReadinessSnapshotInput,
): PostHogEventMap["agent_readiness_audit_snapshot"] {
  const agentEngine = safeSlug(input.agentEngine);

  return compactPayload({
    agent_engine: agentEngine,
    agent_engine_family: agentEngineFamily(agentEngine),
    agent_session_class: "agent",
    agent_run_id: safeSlug(input.agentRunId),
    agent_surface: "repo",
    audit_artifact_kind: enumValue(input.artifactKind, artifactKinds, "csv"),
    audit_artifact_path: safeArtifactPath(input.artifactPath),
    audit_category: safeSlug(input.category),
    audit_confidence: enumValue(input.confidence, auditConfidences, "low"),
    audit_lane: enumValue(input.lane, auditLanes, "none"),
    audit_lens_slug: safeSlug(input.lensSlug),
    audit_priority: enumValue(input.priority, auditPriorities, "P3"),
    audit_verdict: enumValue(input.verdict, auditVerdicts, "NOT_DETERMINABLE"),
    regression_status: enumValue(input.regressionStatus, regressionStatuses, "configured_repo"),
    snapshot_check_count: numberValue(input.checkCount),
    snapshot_failed_count: numberValue(input.failedCount),
    snapshot_na_count: numberValue(input.naCount),
    snapshot_not_determinable_count: numberValue(input.notDeterminableCount),
    snapshot_ticket_count: numberValue(input.ticketCount),
    source_event_kind: enumValue(input.sourceEventKind, sourceEventKinds, "manual_import"),
    taxonomy_version: AGENT_READINESS_TAXONOMY_VERSION,
    tracking_surface_state: enumValue(input.trackingSurfaceState, trackingSurfaceStates, "defined_repo"),
  });
}

export function buildAgentReadinessOperatorFeedbackPayload(
  input: AgentReadinessOperatorFeedbackInput,
): PostHogEventMap["agent_readiness_operator_feedback"] {
  return compactPayload({
    ...buildAgentReadinessAuditSnapshotPayload(input),
    operator_feedback_state: enumValue(input.operatorFeedbackState, operatorFeedbackStates, "not_requested"),
  });
}
